var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useReducer, useRef } from "react";
import { suspendResource } from "../../utils";
import { usePortal } from "../../Providers/PortalProvider";
import { usePictureSets } from "../hooks/usePictureSets";
import { useAuth } from "./AuthProvider";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from "react-i18next";
import { isServerProductOrderLine, } from "../types/ShoppingCart";
import { productUuidIsDigitalFileProduct, productUuidIsDigitalSetProduct, } from "@/react/Webshop/providers/PriceListProvider";
import { useShoppingCartApiClient } from "@/react/Webshop/providers/ShoppingCartClientProvider";
const ShoppingCartContext = React.createContext(undefined);
let initialCartResource = null;
function getCartSuspendResource(shoppingCartClient) {
    if (!initialCartResource) {
        initialCartResource = suspendResource(shoppingCartClient.getInitialShoppingCart());
    }
    return initialCartResource;
}
const defaultState = {
    isLoading: false,
    isUpdating: false,
    items: [],
    totalPrice: 0,
    qualifyForDigitalRebate: false,
    qualifyForPromotedPrice: false,
    summary: [],
    error: null,
};
export const serverOrderLinesToServerProductOrderLines = (orderLines) => {
    return orderLines.filter((item) => {
        return item.type === 0 && "uuid" in item;
    });
};
const serverOrderLinesToAnyOrderLines = (orderLines, state) => {
    const productOrderLines = serverOrderLinesToServerProductOrderLines(orderLines);
    return productOrderLines.map((item) => {
        const clientItem = state.items.find((i) => (i === null || i === void 0 ? void 0 : i.uuid) && i.type === 0 && (i === null || i === void 0 ? void 0 : i.uuid) === item.uuid);
        return Object.assign(Object.assign({}, item), { isUpdating: clientItem ? clientItem.isUpdating : false, isDeleting: clientItem ? clientItem.isDeleting : false });
    });
};
const serverDataToState = (serverData, state, portal) => {
    const newState = Object.assign(Object.assign({}, state), { isLoading: false, error: null, items: serverOrderLinesToAnyOrderLines(serverData.orderLines, state) });
    return Object.assign(Object.assign({}, newState), { qualifyForDigitalRebate: checkQualifyForDigitalRebate(newState, portal), qualifyForPromotedPrice: serverData.orderLines.some((item) => isServerProductOrderLine(item) && item.product.type == 200), summary: serverData.orderLines.filter((ol) => ol.type != 0), totalPrice: serverData.summary.totalPrice, isUpdating: newState.items.some((item) => item.isUpdating) });
};
const cartReducer = (state, action) => {
    let newState = null;
    switch (action.type) {
        case "RELOAD_START":
            return Object.assign(Object.assign({}, state), { isLoading: true });
        case "RELOAD_FAILURE":
            return Object.assign(Object.assign({}, state), { isLoading: false, isUpdating: false, error: action.error });
        case "UPDATE_SUCCESS":
            return serverDataToState(action.data, state, action.portal);
        case "UPDATE_ITEM_SUCCESS":
            newState = Object.assign(Object.assign({}, state), { items: state.items.map((item) => item.uuid === action.uuid ? Object.assign(Object.assign({}, item), { isUpdating: false }) : item) });
            return serverDataToState(action.data, newState, action.portal);
        case "DELETE_ITEM_SUCCESS":
            newState = Object.assign(Object.assign({}, state), { items: state.items.map((item) => item.uuid === action.uuid ? Object.assign(Object.assign({}, item), { isDeleting: false }) : item) });
            return serverDataToState(action.data, newState, action.portal);
        case "UPDATE_ITEM_START":
            return Object.assign(Object.assign({}, state), { items: state.items.map((item) => item.uuid === action.uuid ? Object.assign(Object.assign({}, item), { isUpdating: true }) : item), isUpdating: true });
        case "UPDATE_MULTIPLE_ITEMS_START":
            return Object.assign(Object.assign({}, state), { items: state.items.map((item) => action.uuids.indexOf(item.uuid) >= 0
                    ? Object.assign(Object.assign({}, item), { isUpdating: true }) : item), isUpdating: true });
        case "UPDATE_MULTIPLE_ITEMS_SUCCESS":
            newState = Object.assign(Object.assign({}, state), { items: state.items.map((item) => action.uuids.indexOf(item.uuid) >= 0
                    ? Object.assign(Object.assign({}, item), { isUpdating: false }) : item) });
            return serverDataToState(action.data, newState, action.portal);
        case "UPDATE_MULTIPLE_ITEMS_FAILURE":
            return Object.assign(Object.assign({}, state), { items: state.items.map((item) => action.uuids.indexOf(item.uuid) >= 0
                    ? Object.assign(Object.assign({}, item), { isUpdating: false }) : item), isUpdating: false });
        case "DELETE_ITEM_START":
            return Object.assign(Object.assign({}, state), { items: state.items.map((item) => item.uuid === action.uuid ? Object.assign(Object.assign({}, item), { isDeleting: true }) : item), isUpdating: true });
        case "UPDATE_FAILURE":
            return Object.assign(Object.assign({}, state), { items: state.items.map((item) => {
                    // If update fails, the queue is cleared, so all isUpdating is cleared:
                    return Object.assign(Object.assign({}, item), { isUpdating: false });
                }), isUpdating: false });
        case "DELETE_FAILURE":
            return Object.assign(Object.assign({}, state), { items: state.items.map((item) => item.uuid === action.uuid ? Object.assign(Object.assign({}, item), { isDeleting: false }) : item), isUpdating: false });
        default:
            return state;
    }
};
const ShoppingCartProvider = ({ children }) => {
    const pictureSets = usePictureSets();
    // Hack as we don't use picture sets on desktop:
    let updatePictureSets = () => { };
    if (pictureSets) {
        updatePictureSets = pictureSets.update;
    }
    const { user } = useAuth();
    const [t] = useTranslation();
    const shoppingCartClient = useShoppingCartApiClient();
    const initialCartSuspendResource = getCartSuspendResource(shoppingCartClient);
    const initialData = useRef(initialCartSuspendResource.read());
    const portal = usePortal();
    const [state, dispatch] = useReducer(cartReducer, serverDataToState(initialData.current, defaultState, portal));
    const isFirstRun = useRef(true);
    // updateQueue is a reference to a Promise that represents a queue of updates.
    // Each time updateQuantity or deleteItem is called, a new update is added to the end of the queue.
    // The updates are then processed one at a time, in the order they were added to the queue.
    // This ensures that all updates are sent to the server and that the client state is synced with the server state,
    // even when multiple updates are made at the same time.
    const updateQueue = useRef(Promise.resolve());
    const updateCart = () => __awaiter(void 0, void 0, void 0, function* () {
        dispatch({ type: "RELOAD_START" });
        let data = null;
        try {
            data = yield shoppingCartClient.update();
            dispatch({ type: "UPDATE_SUCCESS", data, portal });
        }
        catch (err) {
            dispatch({ type: "RELOAD_FAILURE", error: err });
        }
        return data;
    });
    useEffect(() => {
        // First run use suspend, so don't fetch the cart again:
        if (isFirstRun.current) {
            isFirstRun.current = false;
            return;
        }
        updateCart();
    }, [user]);
    const changeItemQuantity = (orderLine, quantity) => {
        updateQueue.current = updateQueue.current.then(() => __awaiter(void 0, void 0, void 0, function* () {
            dispatch({ type: "UPDATE_ITEM_START", uuid: orderLine.uuid });
            try {
                const data = yield shoppingCartClient.changeItemQuantity(orderLine.uuid, quantity);
                if (data.cart) {
                    dispatch({
                        type: "UPDATE_ITEM_SUCCESS",
                        data: data.cart,
                        portal,
                        uuid: orderLine.uuid,
                    });
                    if (window.isEpmsAnalyticsFeatureEnabled && window.epmsAnalytics) {
                        window.epmsAnalytics.onCartChange(data);
                    }
                }
            }
            catch (_a) {
                toast.error(t("Updated item failed"));
                // Dispatch the failure action with the original quantity
                dispatch({ type: "UPDATE_FAILURE", uuid: orderLine.uuid });
                // Clear the queue
                updateQueue.current = Promise.resolve();
            }
        }));
    };
    const updateItem = (orderLine) => __awaiter(void 0, void 0, void 0, function* () {
        dispatch({ type: "UPDATE_ITEM_START", uuid: orderLine.uuid });
        try {
            const data = yield shoppingCartClient.updateItem(orderLine);
            if (data.cart) {
                dispatch({
                    type: "UPDATE_ITEM_SUCCESS",
                    data: data.cart,
                    portal,
                    uuid: orderLine.uuid,
                });
                if (window.isEpmsAnalyticsFeatureEnabled && window.epmsAnalytics) {
                    window.epmsAnalytics.onCartChange(data);
                }
            }
            return data;
        }
        catch (_a) {
            toast.error(t("Updated item failed"));
            // Dispatch the failure action with the original quantity
            dispatch({ type: "UPDATE_FAILURE", uuid: orderLine.uuid });
        }
    });
    /**
     * Use this when changing quality/delete actions where there may be race conditions
     */
    const queuedUpdateItem = (orderLine) => {
        updateQueue.current = updateQueue.current.then(() => __awaiter(void 0, void 0, void 0, function* () {
            dispatch({ type: "UPDATE_ITEM_START", uuid: orderLine.uuid });
            try {
                const data = yield shoppingCartClient.updateItem(orderLine);
                if (data.cart) {
                    dispatch({
                        type: "UPDATE_ITEM_SUCCESS",
                        data: data.cart,
                        portal,
                        uuid: orderLine.uuid,
                    });
                    if (window.isEpmsAnalyticsFeatureEnabled && window.epmsAnalytics) {
                        window.epmsAnalytics.onCartChange(data);
                    }
                }
            }
            catch (_a) {
                toast.error(t("Updated item failed"));
                // Dispatch the failure action with the original quantity
                dispatch({ type: "UPDATE_FAILURE", uuid: orderLine.uuid });
                // Clear the queue
                updateQueue.current = Promise.resolve();
            }
        }));
    };
    const addItem = (item) => __awaiter(void 0, void 0, void 0, function* () {
        // The error handling is done where the method is used:
        const data = yield shoppingCartClient.addItem(item);
        if (data.newOrderLines) {
            if (window.isEpmsAnalyticsFeatureEnabled && window.epmsAnalytics) {
                window.epmsAnalytics.onAddToCart(data.newOrderLines);
            }
        }
        if (data.cart) {
            dispatch({ type: "UPDATE_SUCCESS", data: data.cart, portal });
        }
        return data;
    });
    const addMultipleItems = (item) => __awaiter(void 0, void 0, void 0, function* () {
        const data = yield shoppingCartClient.addMultipleItems(item);
        if (data.newOrderLines) {
            if (window.isEpmsAnalyticsFeatureEnabled && window.epmsAnalytics) {
                window.epmsAnalytics.onAddToCart(data.newOrderLines);
            }
        }
        if (data.cart) {
            dispatch({ type: "UPDATE_SUCCESS", data: data.cart, portal });
        }
        return data;
    });
    const deleteItem = (item) => __awaiter(void 0, void 0, void 0, function* () {
        updateQueue.current = updateQueue.current.then(() => __awaiter(void 0, void 0, void 0, function* () {
            dispatch({ type: "DELETE_ITEM_START", uuid: item.uuid });
            try {
                const data = yield shoppingCartClient.deleteItem(item);
                if (data.cart) {
                    dispatch({
                        type: "DELETE_ITEM_SUCCESS",
                        uuid: item.uuid,
                        data: data.cart,
                        portal,
                    });
                    updatePictureSets();
                    if (window.isEpmsAnalyticsFeatureEnabled && window.epmsAnalytics) {
                        window.epmsAnalytics.onCartChange(data);
                    }
                }
            }
            catch (_a) {
                // Dispatch the failure action with the original quantity
                toast.error(t("Delete item failed"));
                dispatch({ type: "DELETE_FAILURE", uuid: item.uuid });
                // Clear the queue
                updateQueue.current = Promise.resolve();
            }
        }));
    });
    const replaceCart = (cart) => {
        dispatch({ type: "UPDATE_SUCCESS", data: cart, portal });
    };
    return (React.createElement(ShoppingCartContext.Provider, { value: {
            addItem,
            items: state.items,
            summary: state.summary,
            isLoading: state.isLoading,
            isUpdating: state.isUpdating,
            error: state.error,
            deleteItem,
            updateItem,
            queuedUpdateItem,
            changeItemQuantity,
            totalPrice: state.totalPrice,
            addMultipleItems,
            qualifyForDigitalRebate: state.qualifyForDigitalRebate,
            qualifyForPromotedPrice: state.qualifyForPromotedPrice,
            updateCart,
            replaceCart,
        } },
        children,
        React.createElement(ToastContainer, null)));
};
const useShoppingCart = () => {
    const context = React.useContext(ShoppingCartContext);
    if (!context) {
        throw new Error("useShoppingCart must be used within a ShoppingCartProvider");
    }
    return context;
};
export { ShoppingCartProvider, useShoppingCart };
const checkQualifyForDigitalRebate = (cart, portal) => {
    const { digitalRebate } = portal.webshop;
    const priceWithoutDigitalFiles = cart.items.reduce((totalSum, orderLine) => {
        if (orderLine.type != 0) {
            return totalSum;
        }
        if (productUuidIsDigitalFileProduct(orderLine.product.uuid) ||
            productUuidIsDigitalSetProduct(orderLine.product.uuid)) {
            return totalSum;
        }
        return totalSum + orderLine.actualLinePrice;
    }, 0);
    if (digitalRebate.priceThreshold > 0 &&
        priceWithoutDigitalFiles > digitalRebate.priceThreshold) {
        return true;
    }
    // Okey if we have a package with more than one product:
    return cart.items.some((orderLine) => {
        // FIXME: This is a bad type guard as we're unable to rename the
        // package property of PackageOrderLine. Why can't we check for
        // orderLine.product.type == 200?
        if (orderLine.type === 0 &&
            "package" in orderLine &&
            orderLine.package.packageItems.length > 1) {
            return true;
        }
        return false;
    });
};
